import {
  type ReactNode,
  type FC,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';

import {
  FontFaceDeclarationsQuery,
  FontFaceDeclarationsQueryVariables,
} from 'client/graphql/types/operations';

import useDebouncedEffect from 'utils/useDebouncedEffect';

export const FONT_FACE_DECLARATIONS_QUERY = gql`
  query FontFaceDeclarationsQuery($faceIds: [Int!]) {
    fontFaces(ids: $faceIds) {
      id
      cssFontFaceDeclaration
    }
  }
`;

interface Props {
  children: ReactNode;
}

const FontFaceDeclarationsContext = createContext<{
  unfetchedFontFaceIds: number[];
  fetchFontFaceDeclaration: (faceId: number) => void;
}>({ unfetchedFontFaceIds: [], fetchFontFaceDeclaration: () => {} });

export const useFetchFontFaceDeclaration = () =>
  useContext(FontFaceDeclarationsContext);

const FamiliesFontFaceDeclarations: FC<Props> = (props) => {
  const { children } = props;

  const client = useApolloClient();

  // Remove font faces from cache on unmount.
  useEffect(() => {
    return () => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'fontFaces' });
      client.cache.gc();
    };
  }, [client]);

  const [faceIds, setFaceIds] = useState<number[]>([]);

  // Apollo's "useLazyQuery" doesn't support pagination (i.e. fetchMore), so
  // instead we need to use a regular query hook with "skip" set to true.
  const { data, fetchMore } = useQuery<
    FontFaceDeclarationsQuery,
    FontFaceDeclarationsQueryVariables
  >(FONT_FACE_DECLARATIONS_QUERY, {
    skip: faceIds.length === 0,
  });

  const unfetchedFontFaceIds = useMemo(() => {
    return faceIds.filter(
      (id) => !data?.fontFaces?.some((face) => face.id === id),
    );
  }, [faceIds, data]);

  const fetchFontFaceDeclaration = useCallback(
    (faceId: number) => {
      setFaceIds((state) => {
        if (state.includes(faceId)) {
          return state;
        }

        return [...state, faceId].sort();
      });
    },
    [setFaceIds],
  );

  useDebouncedEffect(
    () => {
      if (unfetchedFontFaceIds.length > 0) {
        fetchMore({
          variables: { faceIds: unfetchedFontFaceIds },
          updateQuery: (
            previousResult: FontFaceDeclarationsQuery,
            { fetchMoreResult },
          ) => {
            if (previousResult?.fontFaces == null) {
              return fetchMoreResult;
            }

            return {
              ...previousResult,
              fontFaces: [
                ...previousResult.fontFaces,
                ...fetchMoreResult?.fontFaces?.filter(
                  (face) =>
                    !previousResult.fontFaces.find(({ id }) => face.id === id),
                ),
              ],
            };
          },
        });
      }
    },
    0,
    [unfetchedFontFaceIds, fetchMore],
  );

  const value = useMemo(() => {
    return { unfetchedFontFaceIds, fetchFontFaceDeclaration };
  }, [unfetchedFontFaceIds, fetchFontFaceDeclaration]);

  return (
    <FontFaceDeclarationsContext.Provider value={value}>
      {children}

      {data?.fontFaces?.map((face) =>
        face.cssFontFaceDeclaration != null ? (
          <style
            key={face.id}
            dangerouslySetInnerHTML={{ __html: face.cssFontFaceDeclaration }}
          />
        ) : null,
      )}
    </FontFaceDeclarationsContext.Provider>
  );
};

export default FamiliesFontFaceDeclarations;
